import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  ArrowUp,
  ArrowUpRight,
  Banknote,
  ClipboardList,
  Gauge,
  Landmark,
  MessageCircle,
  RotateCcw,
  Sparkles,
  Sunrise,
  TriangleAlert,
  Wrench,
  X,
} from 'lucide-react';
import { assistantAPI } from '../../lib/api';
import { useEstMobile } from '../../hooks/useEstMobile';
import { cn } from '../../lib/utils';
import { messageErreur } from '../../lib/erreurs';
import { TexteAssistant } from './texteAssistant';
import { AssistantDraftCard, valeursInitiales } from './AssistantDraftCard';

/**
 * Assistant de la plateforme (spec 043): le bot WhatsApp, dans l'application.
 *
 * Un bouton rond en bas à droite ouvre un panneau de discussion. Mêmes
 * réponses que le bot (rubriques, fiche d'un camion, synthèse de l'IA), mêmes
 * droits; une saisie devient un brouillon à vérifier, écrit seulement au
 * bouton « Confirmer ».
 *
 * La discussion ne vit que dans l'onglet ouvert (`sessionStorage`): rien n'est
 * gardé sur le serveur, et fermer l'onglet l'efface.
 */

export const CLE_SESSION = 'ecarbu.assistant.v1';
const SANS_TITRE = new Set(['synthese', 'reponse', 'choix', 'help', 'info', 'draft', 'ok', 'degraded', 'menu', 'saisir']);
/** Réponses rédigées par l'IA, avec le nom des données lues en pied de bulle. */
const REDIGE_PAR_IA = new Set(['synthese', 'reponse']);
const HISTORIQUE_TOURS = 8;
const HISTORIQUE_CARACTERES = 1500;

/** Les derniers échanges de l'onglet, en texte seul, pour une question de suite (« et le mois dernier ? »). */
export const historique = (messages) =>
  messages
    .map((message) => {
      if (message.role === 'user') return { role: 'user', text: message.text };
      if (message.reply?.text && !message.error) return { role: 'assistant', text: message.reply.text };
      return null;
    })
    .filter((tour) => tour && String(tour.text || '').trim())
    .slice(-HISTORIQUE_TOURS)
    .map((tour) => ({ ...tour, text: String(tour.text).slice(0, HISTORIQUE_CARACTERES) }));
const MAX_MESSAGES = 60;
const EASE = [0.22, 1, 0.36, 1];

const ICONES_SUGGESTION = {
  brief: Sunrise,
  etat: Gauge,
  douane: Landmark,
  pannes: Wrench,
  creances: Banknote,
  stocks: ClipboardList,
  'signaler une panne': TriangleAlert,
  'enregistrer un frais': Banknote,
};

const nouvelId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID().replace(/-/g, '');
  } catch {
    /* navigateur ancien */
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
};

const lireSession = () => {
  try {
    const brut = window.sessionStorage.getItem(CLE_SESSION);
    const lu = brut ? JSON.parse(brut) : null;
    return Array.isArray(lu?.messages) ? lu.messages : [];
  } catch {
    return [];
  }
};

const ecrireSession = (messages) => {
  try {
    window.sessionStorage.setItem(CLE_SESSION, JSON.stringify({ messages: messages.slice(-MAX_MESSAGES) }));
  } catch {
    /* stockage plein ou interdit: la discussion vit en mémoire */
  }
};

const erreurDeReponse = (e) =>
  messageErreur(e, 'Je n’arrive pas à répondre pour le moment. Réessayez dans un instant.');

function Avatar({ petit = false }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full bg-[linear-gradient(135deg,hsl(205_57%_35%),hsl(205_62%_20%))] text-white shadow-sm ring-1 ring-black/5',
        petit ? 'h-7 w-7' : 'h-10 w-10',
      )}
    >
      <Sparkles className={petit ? 'h-3.5 w-3.5' : 'h-5 w-5'} />
    </span>
  );
}

function Pastilles({ items, onChoisir, disabled }) {
  if (!items?.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {items.map((item) => (
        <button
          key={`${item.label}-${item.message}`}
          type="button"
          disabled={disabled}
          onClick={() => onChoisir(item.message, item.label)}
          className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/50 hover:bg-primary/5 disabled:opacity-50"
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

function Liens({ items, onLien }) {
  if (!items?.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {items.map((lien) => (
        <button
          key={lien.to}
          type="button"
          onClick={() => onLien(lien.to)}
          className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/15"
        >
          {lien.label}
          <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}

function Ecrit({ reduire }) {
  return (
    <div className="flex items-end gap-2" role="status" aria-label="L’assistant écrit">
      <Avatar petit />
      <div className="flex items-center gap-1 rounded-2xl rounded-bl-md bg-muted px-3.5 py-3">
        {[0, 1, 2].map((point) => (
          <span
            key={point}
            className={cn('h-1.5 w-1.5 rounded-full bg-muted-foreground/70', !reduire && 'animate-pulse')}
            style={reduire ? undefined : { animationDelay: `${point * 160}ms` }}
          />
        ))}
      </div>
    </div>
  );
}

function Bulle({ message, enCours, onChoisir, onLien, onEtatBrouillon }) {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <p className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-primary px-3.5 py-2 text-sm leading-relaxed text-primary-foreground shadow-sm">
          {message.text}
        </p>
      </div>
    );
  }
  const reponse = message.reply;
  return (
    <div className="flex items-end gap-2" data-message={message.id}>
      <Avatar petit />
      <div className="min-w-0 max-w-[88%] flex-1">
        <div
          className={cn(
            'rounded-2xl rounded-bl-md px-3.5 py-2.5 text-sm text-foreground',
            message.error ? 'bg-destructive/10 text-destructive' : 'bg-muted',
          )}
        >
          <TexteAssistant
            texte={message.error || reponse?.text}
            // Une rubrique commence par son titre (« 🛃 Douanes — … »): il se détache du détail.
            titre={!message.error && Boolean(reponse) && !SANS_TITRE.has(reponse.kind)}
          />
          {reponse?.ai && REDIGE_PAR_IA.has(reponse.kind) && reponse.sources?.length ? (
            <p className="mt-2 flex items-center gap-1 border-t border-border/60 pt-2 text-[11px] text-muted-foreground">
              <Sparkles className="h-3 w-3 text-flame-ink" aria-hidden="true" />
              D’après {reponse.sources.join(', ')}
            </p>
          ) : null}
        </div>
        {reponse?.draft && message.draftState ? (
          <AssistantDraftCard
            draft={reponse.draft}
            etat={message.draftState}
            onEtat={(etat) => onEtatBrouillon(message.id, etat)}
            onLien={onLien}
          />
        ) : null}
        <Liens items={reponse?.links} onLien={onLien} />
        <Pastilles items={reponse?.suggestions} onChoisir={onChoisir} disabled={enCours} />
      </div>
    </div>
  );
}

const enNomPropre = (mot) =>
  String(mot || '')
    .toLowerCase()
    .replace(/(^|[\s'’-])(\p{L})/gu, (_, avant, lettre) => avant + lettre.toUpperCase());

function Accueil({ statut, onChoisir, enCours }) {
  const prenom = enNomPropre(statut?.first_name);
  return (
    <div className="flex flex-col gap-5 px-1 pb-2 pt-3">
      <div className="space-y-1.5">
        <p className="text-xl font-semibold tracking-tight text-foreground">
          {prenom ? `Bonjour ${prenom}` : 'Bonjour'} <span aria-hidden="true">👋</span>
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {statut?.ai
            ? 'Posez une question sur votre flotte comme vous l’écririez à un collègue, ou racontez ce qui s’est passé : je prépare la saisie.'
            : 'Demandez une rubrique, une plaque ou le nom d’un chauffeur. Écrivez « menu » pour tout voir.'}
        </p>
        <p className="text-[11px] text-muted-foreground/80">
          La discussion s’efface quand vous fermez l’onglet.
        </p>
      </div>
      {statut?.suggestions?.length ? (
        <div className="grid grid-cols-2 gap-2">
          {statut.suggestions.map((item, index) => {
            const Icone = ICONES_SUGGESTION[item.message] || (index === 0 && statut.ai ? Sparkles : MessageCircle);
            const vedette = index === 0 && statut.ai;
            return (
              <button
                key={item.message}
                type="button"
                disabled={enCours}
                onClick={() => onChoisir(item.message, item.label)}
                className={cn(
                  'group flex items-center gap-2.5 rounded-2xl border p-2.5 text-left transition-all',
                  'hover:-translate-y-px hover:shadow-md disabled:opacity-60',
                  vedette
                    ? 'col-span-2 border-primary/25 bg-gradient-to-br from-primary/10 to-flame/10 p-3 hover:border-primary/40'
                    : 'border-border bg-card hover:border-primary/40',
                )}
              >
                <span
                  className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-xl',
                    vedette ? 'bg-card text-flame-ink shadow-sm' : 'bg-muted text-primary group-hover:bg-primary/10',
                  )}
                >
                  <Icone className="h-4 w-4" aria-hidden="true" />
                </span>
                <span className="text-sm font-medium leading-snug text-foreground">{item.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export function AssistantChat() {
  const navigate = useNavigate();
  const mobile = useEstMobile();
  const reduire = useReducedMotion();
  const [statut, setStatut] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const [messages, setMessages] = useState(lireSession);
  const [saisie, setSaisie] = useState('');
  const [enCours, setEnCours] = useState(false);
  const fil = useRef(null);
  const champ = useRef(null);
  const bouton = useRef(null);

  useEffect(() => {
    let annule = false;
    assistantAPI
      .status()
      .then((lu) => {
        if (!annule) setStatut(lu || { available: false });
      })
      .catch(() => {
        if (!annule) setStatut({ available: false });
      });
    return () => {
      annule = true;
    };
  }, []);

  useEffect(() => {
    ecrireSession(messages);
  }, [messages]);

  // Une longue réponse (un brouillon, une rubrique) se lit depuis son début: on
  // amène son haut en vue plutôt que le bas du fil.
  const dernier = messages[messages.length - 1];
  useLayoutEffect(() => {
    const zone = fil.current;
    if (!ouvert || !zone) return;
    const element = dernier?.role === 'assistant' ? zone.querySelector(`[data-message="${dernier.id}"]`) : null;
    if (element && element.offsetHeight > zone.clientHeight * 0.6) {
      zone.scrollTop = element.offsetTop - 12;
    } else {
      zone.scrollTop = zone.scrollHeight;
    }
  }, [dernier?.id, enCours, ouvert]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!ouvert) return undefined;
    const timer = setTimeout(() => champ.current?.focus(), 80);
    const echap = (e) => {
      if (e.key === 'Escape') {
        setOuvert(false);
        bouton.current?.focus();
      }
    };
    window.addEventListener('keydown', echap);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('keydown', echap);
    };
  }, [ouvert]);

  // Plein écran sur téléphone: la page derrière ne défile plus.
  useEffect(() => {
    if (!(ouvert && mobile)) return undefined;
    const avant = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = avant;
    };
  }, [ouvert, mobile]);

  const envoyer = useCallback(async (texte, affiche) => {
    const message = String(texte || '').trim();
    if (!message || enCours) return;
    setSaisie('');
    if (champ.current) champ.current.style.height = 'auto';
    const avant = historique(messages);
    setMessages((liste) => [...liste, { id: nouvelId(), role: 'user', text: affiche || message }]);
    setEnCours(true);
    try {
      const reponse = await assistantAPI.message(message, avant);
      setMessages((liste) => [
        ...liste,
        {
          id: nouvelId(),
          role: 'assistant',
          reply: reponse,
          draftState: reponse?.draft
            ? { key: nouvelId(), status: 'editing', values: valeursInitiales(reponse.draft), error: null }
            : null,
        },
      ]);
    } catch (e) {
      setMessages((liste) => [...liste, { id: nouvelId(), role: 'assistant', error: erreurDeReponse(e) }]);
    } finally {
      setEnCours(false);
    }
  }, [enCours, messages]);

  const changerBrouillon = useCallback((id, etat) => {
    setMessages((liste) => liste.map((message) => (message.id === id ? { ...message, draftState: etat } : message)));
  }, []);

  const ouvrirLien = useCallback((to) => {
    navigate(to);
    if (mobile) setOuvert(false);
  }, [navigate, mobile]);

  const recommencer = () => {
    setMessages([]);
    setSaisie('');
    champ.current?.focus();
  };

  if (!statut?.available) return null;

  const surTouche = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      envoyer(saisie);
    }
  };

  const ajusterHauteur = (element) => {
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, 128)}px`;
  };

  const apparition = reduire
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        initial: { opacity: 0, y: 16, scale: 0.97 },
        animate: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, y: 12, scale: 0.98 },
      };

  return (
    <>
      <AnimatePresence>
        {ouvert ? (
          <motion.section
            key="assistant"
            id="assistant-panneau"
            role="dialog"
            aria-modal={mobile ? 'true' : 'false'}
            aria-label="Assistant E-Carbu"
            {...apparition}
            transition={{ duration: reduire ? 0.12 : 0.24, ease: EASE }}
            style={{ transformOrigin: 'bottom right' }}
            className={cn(
              'fixed z-[60] flex flex-col overflow-hidden bg-card text-card-foreground',
              mobile
                ? 'inset-0 h-[100dvh] w-full'
                : 'bottom-24 right-6 h-[min(680px,calc(100vh-8rem))] w-[400px] rounded-[1.75rem] border border-border/70 shadow-[0_24px_60px_-20px_rgba(15,40,70,0.45)]',
            )}
          >
            <header className="relative shrink-0 overflow-hidden bg-[linear-gradient(135deg,hsl(205_57%_35%),hsl(205_62%_20%))] text-white px-4 pb-4 pt-[max(1rem,env(safe-area-inset-top))]">
              <span
                aria-hidden="true"
                className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-flame/30 blur-3xl"
              />
              <div className="relative flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20 backdrop-blur">
                  <Sparkles className="h-5 w-5" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <h2 className="truncate text-[15px] font-semibold leading-tight text-white">Assistant E-Carbu</h2>
                  <p className="mt-0.5 flex items-center gap-1.5 text-xs text-white/80">
                    <span
                      aria-hidden="true"
                      className={cn('h-1.5 w-1.5 rounded-full', statut.ai ? 'bg-emerald-300' : 'bg-white/60')}
                    />
                    {statut.ai ? 'IA activée · comprend vos phrases' : 'Rubriques et fiches de votre flotte'}
                  </p>
                </div>
                {messages.length ? (
                  <button
                    type="button"
                    onClick={recommencer}
                    title="Nouvelle discussion"
                    aria-label="Nouvelle discussion"
                    className="flex h-9 w-9 items-center justify-center rounded-full text-white/90 transition-colors hover:bg-white/15"
                  >
                    <RotateCcw className="h-4 w-4" aria-hidden="true" />
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => {
                    setOuvert(false);
                    bouton.current?.focus();
                  }}
                  aria-label="Fermer l’assistant"
                  className="flex h-9 w-9 items-center justify-center rounded-full text-white/90 transition-colors hover:bg-white/15"
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </header>

            <div
              ref={fil}
              aria-live="polite"
              className="relative flex-1 space-y-4 overflow-y-auto overscroll-contain bg-gradient-to-b from-muted/40 via-background to-background px-4 py-4"
            >
              {messages.length === 0 ? (
                <Accueil statut={statut} onChoisir={envoyer} enCours={enCours} />
              ) : (
                messages.map((message) => (
                  <Bulle
                    key={message.id}
                    message={message}
                    enCours={enCours}
                    onChoisir={envoyer}
                    onLien={ouvrirLien}
                    onEtatBrouillon={changerBrouillon}
                  />
                ))
              )}
              {enCours ? <Ecrit reduire={reduire} /> : null}
            </div>

            <form
              className="shrink-0 border-t border-border/70 bg-card px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3"
              onSubmit={(e) => {
                e.preventDefault();
                envoyer(saisie);
              }}
            >
              <div className="flex items-end gap-2 rounded-2xl border border-border bg-background px-3 py-2 transition-shadow focus-within:border-primary/50 focus-within:ring-4 focus-within:ring-primary/10">
                <label htmlFor="assistant-saisie" className="sr-only">Votre message</label>
                <textarea
                  id="assistant-saisie"
                  ref={champ}
                  rows={1}
                  maxLength={1000}
                  value={saisie}
                  placeholder={statut.ai ? 'Écrivez votre question…' : 'Une rubrique, une plaque, un nom…'}
                  onChange={(e) => {
                    setSaisie(e.target.value);
                    ajusterHauteur(e.target);
                  }}
                  onKeyDown={surTouche}
                  className="max-h-32 min-h-[1.5rem] flex-1 resize-none border-0 bg-transparent py-1 text-sm leading-relaxed text-foreground outline-none placeholder:text-muted-foreground/70 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
                <button
                  type="submit"
                  disabled={!saisie.trim() || enCours}
                  aria-label="Envoyer"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-35"
                >
                  <ArrowUp className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
              <p className="mt-2 text-center text-[11px] leading-snug text-muted-foreground">
                Rien n’est enregistré sans votre confirmation.
              </p>
            </form>
          </motion.section>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {!(ouvert && mobile) ? (
          <motion.button
            key="bouton"
            ref={bouton}
            type="button"
            initial={reduire ? false : { opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2, ease: EASE }}
            onClick={() => setOuvert((valeur) => !valeur)}
            aria-expanded={ouvert}
            aria-controls="assistant-panneau"
            aria-label={ouvert ? 'Fermer l’assistant' : 'Ouvrir l’assistant'}
            title="Assistant"
            className={cn(
              'group fixed right-4 z-[60] flex h-14 w-14 items-center justify-center rounded-full',
              'bottom-[calc(5.25rem+env(safe-area-inset-bottom))] lg:bottom-6 lg:right-6',
              'bg-[linear-gradient(135deg,hsl(205_57%_35%),hsl(205_62%_20%))] text-white ring-1 ring-black/5',
              'shadow-[0_12px_28px_-8px_hsl(205_57%_35%/0.65)] transition-[transform,box-shadow] duration-200',
              'hover:-translate-y-0.5 hover:shadow-[0_16px_34px_-8px_hsl(205_57%_35%/0.8)]',
              'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/30',
            )}
          >
            <span className="relative flex items-center justify-center">
              {ouvert ? (
                <X className="h-6 w-6" aria-hidden="true" />
              ) : statut.ai ? (
                <Sparkles className="h-6 w-6 transition-transform duration-200 group-hover:rotate-12" aria-hidden="true" />
              ) : (
                <MessageCircle className="h-6 w-6" aria-hidden="true" />
              )}
            </span>
            {!ouvert && statut.ai ? (
              <span
                aria-hidden="true"
                className="absolute right-0.5 top-0.5 h-3.5 w-3.5 rounded-full border-2 border-card bg-flame"
              />
            ) : null}
          </motion.button>
        ) : null}
      </AnimatePresence>
    </>
  );
}

export default AssistantChat;
